import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Account } from '../types/accounting';

type AccountingMode = 'SYSCOHADA' | 'IFRS';
type AccountingFramework = 'SYSCOHADA_NORMAL' | 'SYSCOHADA_MINIMAL' | 'IFRS';

interface AccountingState {
  mode: AccountingMode;
  framework: AccountingFramework | null;
  /** The user's actual chart of accounts for the current fiscal year, cached locally */
  chartAccounts: Account[];
  chartFiscalYearId: string | null;
  chartLoading: boolean;
  chartLastSynced: number | null;
  setMode: (mode: AccountingMode) => void;
  setFramework: (framework: AccountingFramework) => void;
  setChartAccounts: (accounts: Account[], fiscalYearId: string) => void;
  clearChart: () => void;
  setChartLoading: (loading: boolean) => void;
}

export const useAccountingStore = create<AccountingState>()(
  persist(
    (set) => ({
      mode: 'SYSCOHADA',
      framework: null,
      chartAccounts: [],
      chartFiscalYearId: null,
      chartLoading: false,
      chartLastSynced: null,
      setMode: (mode) => set({ mode }),
      setFramework: (framework) => set({ 
        framework,
        mode: framework?.includes('IFRS') ? 'IFRS' : 'SYSCOHADA',
      }),
      setChartAccounts: (accounts, fiscalYearId) => set({ 
        chartAccounts: accounts, 
        chartFiscalYearId: fiscalYearId,
        chartLastSynced: Date.now(),
      }),
      clearChart: () => set({ chartAccounts: [], chartFiscalYearId: null, chartLastSynced: null }),
      setChartLoading: (loading) => set({ chartLoading: loading }),
    }),
    {
      name: 'accounting-storage',
      partialize: (state) => ({
        mode: state.mode,
        framework: state.framework,
        // Don't persist large chart array in localStorage
        chartFiscalYearId: state.chartFiscalYearId,
        chartLastSynced: state.chartLastSynced,
      }),
    }
  )
);